// src/pages/Booking/RecapPage.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBooking } from '../../contexts/BookingContext';
import { useAuth } from '../../contexts/AuthContext';
import { formatPriceFCFA } from '../../utils/formatters';
import bookingService from '../../services/bookingService';
import { MapPin, Home, AlertTriangle, CheckCircle } from 'lucide-react';
import { getSalonImage, SALON_FALLBACK_IMAGE } from '../../constants/salonImages';
import './RecapPage.css';

const RecapPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    selectedServices,
    selectedSalon,
    selectedDate,
    selectedTime,
    loading: contextLoading
  } = useBooking();

  console.log('service_type:', selectedSalon?.service_type);
console.log('transport_fee_min:', selectedSalon?.transport_fee_min);
console.log('wave_phone:', selectedSalon?.wave_phone);

  const [clientNotes, setClientNotes]             = useState('');
  const [locationChoice, setLocationChoice]       = useState('salon'); // 'salon' | 'home'
  const [quartier, setQuartier]                   = useState('');
  const [homeConfirmed, setHomeConfirmed]         = useState(false);
  const [loading, setLoading]                     = useState(false);
  const [error, setError]                         = useState('');

  // Calculs
  const totalDuration = selectedServices.reduce((sum, s) => sum + s.duration, 0);
  const totalPrice    = selectedServices.reduce((sum, s) => sum + parseFloat(s.price), 0);

  const needsLocationChoice = selectedSalon?.service_type === 'both' || selectedSalon?.service_type === 'home';
  const isHomeChosen        = needsLocationChoice && locationChoice === 'home';

  // Numéro de paiement pour les frais de déplacement
  const paymentNumber = selectedSalon?.wave_phone || selectedSalon?.orange_phone || null;
  const paymentMethod = selectedSalon?.wave_phone ? 'Wave' : selectedSalon?.orange_phone ? 'Orange Money' : null;

  // Frais de déplacement
  const transportFee = selectedSalon?.transport_fee_min
    ? `${parseInt(selectedSalon.transport_fee_min).toLocaleString('fr-FR')} FCFA`
    : 'À discuter avec le salon';

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins  = minutes % 60;
    if (hours > 0 && mins > 0) return `${hours}h${mins}`;
    if (hours > 0) return `${hours}h`;
    return `${mins}min`;
  };

  const formatDate = (dateStr) => {
    const date    = new Date(dateStr);
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('fr-FR', options);
  };

  // Construire client_notes final
  const buildClientNotes = () => {
    const parts = [];

    if (isHomeChosen) {
      let deplPart = 'DEPLACEMENT';
      if (quartier.trim()) deplPart += ` | Quartier: ${quartier.trim()}`;
      if (selectedSalon?.transport_fee_min) {
        deplPart += ` | Frais: ${parseInt(selectedSalon.transport_fee_min).toLocaleString('fr-FR')} FCFA`;
      }
      parts.push(deplPart);
    }

    if (clientNotes.trim()) parts.push(clientNotes.trim());

    return parts.join('\n') || null;
  };

  // Remplace handleValidate dans RecapPage.js par ceci :

  // ✅ VERSION CORRIGÉE — Remplace handleValidate dans RecapPage.js par ceci :

  const handleValidate = async () => {
    setError('');

    if (isHomeChosen && !homeConfirmed) {
      setError('Veuillez confirmer que vous avez compris les conditions de déplacement.');
      return;
    }

    setLoading(true);

    try {
      const reservationData = {
        salonId:         selectedSalon.id,
        services:        selectedServices.map(s => ({
          id:       s.id,
          name:     s.name,
          price:    parseFloat(s.price),
          duration: s.duration,
        })),
        reservationDate: selectedDate,
        reservationTime: selectedTime,
        clientNotes:     buildClientNotes(),
      };

      // 1. Créer la réservation
      const resaResult = await bookingService.createReservation(reservationData);
      if (!resaResult.success) {
        setError(resaResult.message || 'Erreur lors de la création de la réservation');
        setLoading(false);
        return;
      }

      const reservationId = resaResult.data?.id;

      // 2. Récupérer l'escrow — le backend calcule le vrai montant selon le % admin
      const escrowResult = await bookingService.getEscrow(reservationId);
      if (!escrowResult.success || !escrowResult.escrow) {
        setError('Erreur lors de la récupération des informations de paiement');
        setLoading(false);
        return;
      }

      const escrow = escrowResult.escrow;

      // 3. Naviguer vers la page de paiement avec les vraies données
      navigate('/booking/payment', {
        state: {
          reservationId,
          depositPercent: escrow.deposit_percentage,
          depositAmount:  escrow.deposit_amount,
          totalPrice,
          salonName:   selectedSalon.name,
          wavePhone:   selectedSalon.wave_phone   || null,
          orangePhone: selectedSalon.orange_phone || null,
        }
      });

    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  if (contextLoading) {
    return (
      <div className="recap-page">
        <div className="recap-container">
          <div className="recap-loading">
            <div className="spinner" />
            <p>Chargement...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!selectedSalon || selectedServices.length === 0 || !selectedDate || !selectedTime) {
    return (
      <div className="recap-page">
        <div className="recap-error">
          <h2>Données manquantes</h2>
          <p>Veuillez sélectionner des services et un créneau</p>
          <button onClick={() => navigate('/booking/new')}>Retour au booking</button>
        </div>
      </div>
    );
  }

  return (
    <div className="recap-page">
      <div className="recap-container">

        {/* Header */}
        <div className="recap-header">
          <button type="button" className="back-btn" onClick={() => navigate(-1)}>← Retour</button>
          <h1>Récapitulatif</h1>
          <p className="recap-header-sub">Vérifiez les détails avant de payer l’acompte</p>
        </div>

        <div className="recap-content">

          {/* Salon */}
          <div className="recap-section salon-section">
            <h2>📍 Salon</h2>
            <div className="salon-card">
              <img
                src={getSalonImage(selectedSalon)}
                alt={selectedSalon.name}
                className="salon-image"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = SALON_FALLBACK_IMAGE;
                }}
              />
              <div className="salon-info">
                <h3>{selectedSalon.name}</h3>
                <p className="salon-address">📍 {selectedSalon.address}, {selectedSalon.city}</p>
              </div>
            </div>
          </div>

          {/* ── SECTION LIEU (si salon fait déplacement) ── */}
          {needsLocationChoice && (
            <div className="recap-section">
              <h2>📍 Lieu de la prestation</h2>

              {/* Choix au salon ou à domicile */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>

                {/* Option : Au salon */}
                {selectedSalon.service_type === 'both' && (
                  <button
                    type="button"
                    onClick={() => { setLocationChoice('salon'); setHomeConfirmed(false); }}
                    style={{
                      flex: 1,
                      padding: '14px 12px',
                      border: `2px solid ${locationChoice === 'salon' ? '#C9936B' : '#E2E8F0'}`,
                      borderRadius: 10,
                      background: locationChoice === 'salon' ? '#FCF8F4' : '#fff',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      fontFamily: 'inherit',
                    }}
                  >
                    <MapPin size={18} color={locationChoice === 'salon' ? '#C9936B' : '#A1A1AA'} />
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#0F172A' }}>Au salon</div>
                      <div style={{ fontSize: 12, color: '#71717A' }}>{selectedSalon.address}</div>
                    </div>
                  </button>
                )}

                {/* Option : À domicile */}
                <button
                  type="button"
                  onClick={() => setLocationChoice('home')}
                  style={{
                    flex: 1,
                    padding: '14px 12px',
                    border: `2px solid ${locationChoice === 'home' ? '#C9936B' : '#E2E8F0'}`,
                    borderRadius: 10,
                    background: locationChoice === 'home' ? '#FCF8F4' : '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    fontFamily: 'inherit',
                  }}
                >
                  <Home size={18} color={locationChoice === 'home' ? '#C9936B' : '#A1A1AA'} />
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#0F172A' }}>À domicile</div>
                    <div style={{ fontSize: 12, color: '#71717A' }}>Le salon se déplace</div>
                  </div>
                </button>

              </div>

              {/* Si domicile choisi */}
              {isHomeChosen && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

                  {/* Champ quartier */}
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: 14, marginBottom: 6, color: '#0F172A' }}>
                      Votre quartier / point de repère
                    </label>
                    <input
                      type="text"
                      value={quartier}
                      onChange={e => setQuartier(e.target.value)}
                      placeholder="Ex: Plateau, près de la cathédrale"
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        border: '1.5px solid #E2E8F0',
                        borderRadius: 9,
                        fontSize: 14,
                        fontFamily: 'inherit',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  {/* Bloc avertissement frais */}
                  <div style={{
                    background: '#FFF8F0',
                    border: '1.5px solid #FDE4C8',
                    borderLeft: '4px solid #F97316',
                    borderRadius: 10,
                    padding: '14px 16px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10, fontWeight: 800, fontSize: 13, color: '#C2600A' }}>
                      <AlertTriangle size={15} />
                      Conditions de déplacement
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 7, fontSize: 13, color: '#475569', marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#71717A' }}>Frais de déplacement minimum</span>
                        <strong style={{ color: '#C2600A' }}>{transportFee}</strong>
                      </div>
                      {paymentNumber && (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#71717A' }}>Numéro {paymentMethod}</span>
                          <strong style={{ color: '#0F172A' }}>{paymentNumber}</strong>
                        </div>
                      )}
                    </div>

                    <p style={{ fontSize: 12, color: '#92400E', lineHeight: 1.6, margin: 0 }}>
                      Les frais de déplacement sont à envoyer directement au salon dès confirmation de votre réservation
                      {paymentNumber ? ` au numéro ${paymentMethod} : ${paymentNumber}` : ''}.
                      Le salon vous contactera pour confirmer votre adresse exacte.
                      Les frais de déplacement ne sont pas pris en charge par la plateforme.
                    </p>

                    {/* Checkbox confirmation */}
                    <label style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      marginTop: 14,
                      cursor: 'pointer',
                      fontSize: 13,
                      color: '#475569',
                      lineHeight: 1.5,
                    }}>
                      <input
                        type="checkbox"
                        checked={homeConfirmed}
                        onChange={e => setHomeConfirmed(e.target.checked)}
                        style={{ marginTop: 2, cursor: 'pointer', width: 16, height: 16, flexShrink: 0 }}
                      />
                      <span>
                        J'ai compris les conditions de déplacement et je m'engage à envoyer les frais minimum dès confirmation de ma réservation.
                      </span>
                    </label>

                  </div>
                </div>
              )}
            </div>
          )}

          {/* Services */}
          <div className="recap-section services-section">
            <h2>✂️ Prestations sélectionnées</h2>
            <div className="services-list">
              {selectedServices.map((service, index) => (
                <div key={index} className="service-item">
                  <div className="service-info">
                    <h4>{service.name}</h4>
                    <p className="service-duration">⏱️ {formatDuration(service.duration)}</p>
                  </div>
                  <div className="service-price">{formatPriceFCFA(service.price)}</div>
                </div>
              ))}
            </div>
            <div className="services-total">
              <span>Durée totale :</span>
              <strong>{formatDuration(totalDuration)}</strong>
            </div>
          </div>

          {/* Date & Heure */}
          <div className="recap-section datetime-section">
            <h2>📅 Date et heure</h2>
            <div className="datetime-card">
              <div className="datetime-item">
                <span className="datetime-label">Date :</span>
                <strong>{formatDate(selectedDate)}</strong>
              </div>
              <div className="datetime-item">
                <span className="datetime-label">Heure :</span>
                <strong>{selectedTime}</strong>
              </div>
            </div>
          </div>

          {/* Client */}
          <div className="recap-section client-section">
            <h2>👤 Vos informations</h2>
            <div className="client-card">
              <p><strong>Nom :</strong> {user?.firstName} {user?.lastName}</p>
              <p><strong>Téléphone :</strong> {user?.phone}</p>
            </div>
          </div>

          {/* Notes */}
          <div className="recap-section notes-section">
            <h2>📝 Instructions / Notes (optionnel)</h2>
            <textarea
              className="notes-textarea"
              placeholder="Ajoutez des instructions ou des préférences..."
              value={clientNotes}
              onChange={e => setClientNotes(e.target.value)}
              maxLength={500}
              rows={4}
            />
            <p className="char-count">{clientNotes.length}/500</p>
          </div>

          {/* Erreur */}
          {error && (
            <div className="error-message">⚠️ {error}</div>
          )}

          {/* Prix Total */}
          <div className="recap-total">
            <div className="total-content">
              <span>Total à payer :</span>
              <strong className="total-price">{formatPriceFCFA(totalPrice)}</strong>
            </div>
            {isHomeChosen && selectedSalon?.transport_fee_min && (
              <p style={{ fontSize: 12, color: '#71717A', margin: '4px 0 0', textAlign: 'right' }}>
                + {transportFee} de frais de déplacement (à verser directement au salon)
              </p>
            )}
          </div>

          {/* Actions sticky */}
          <div className="recap-actions">
            <button type="button" className="btn-secondary" onClick={() => navigate(-1)} disabled={loading}>
              Modifier
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={handleValidate}
              disabled={loading || (isHomeChosen && !homeConfirmed)}
            >
              {loading ? 'Validation…' : 'Valider et payer'}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default RecapPage;